(() => {
  const readConfig = () => {
    const element = document.getElementById('cloudless-legal-config');
    if (!element) return {};

    try {
      return JSON.parse(element.textContent);
    } catch (_error) {
      return {};
    }
  };

  const slugify = (value) => value
    .toLocaleLowerCase(document.documentElement.lang || undefined)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const createContact = (config) => {
    if (!config.questionsHeading || !config.questionsText) return null;

    const card = document.createElement('aside');
    card.className = 'cl-legal-contact';

    const heading = document.createElement('strong');
    heading.textContent = config.questionsHeading;
    card.append(heading);

    const paragraph = document.createElement('p');
    paragraph.append(document.createTextNode(`${config.questionsText} `));

    if (config.contactLabel && config.contactUrl) {
      const link = document.createElement('a');
      link.className = 'cl-arrow-link';
      link.href = config.contactUrl;
      link.textContent = config.contactLabel;
      const icon = document.createElement('span');
      icon.className = 'cl-arrow-link__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '↗';
      link.append(document.createTextNode(' '), icon);
      paragraph.append(link);
    }

    card.append(paragraph);
    return card;
  };

  const prepareNativePolicy = (config) => {
    const root = document.querySelector('.shopify-policy__container');
    if (!root || root.dataset.legalReady === 'true') return null;

    const title = root.querySelector('.shopify-policy__title');
    const body = root.querySelector('.shopify-policy__body');
    if (!title || !body) return null;

    root.dataset.legalReady = 'true';
    root.classList.add('cl-legal-page');
    title.classList.add('cl-legal-hero');

    if (config.eyebrow && !title.querySelector('.cl-legal-eyebrow')) {
      const eyebrow = document.createElement('span');
      eyebrow.className = 'cl-legal-eyebrow';
      eyebrow.textContent = config.eyebrow;
      title.prepend(eyebrow);
    }

    const shell = document.createElement('div');
    shell.className = 'cl-legal-shell';

    const navigation = document.createElement('nav');
    navigation.className = 'cl-legal-toc';
    navigation.dataset.legalToc = '';
    navigation.hidden = true;

    shell.append(navigation, body);
    title.after(shell);

    const contact = createContact(config);
    if (contact) body.append(contact);

    return root;
  };

  const NUMBER_PREFIX = /^(?:(?:artikel|article|section)\s+(\d+)\s*[.:–—-]?\s*|(\d+)[.)]\s+)/i;

  const pickHeadings = (content) => {
    const levels = ['h2', 'h3'].map((tag) => [...content.querySelectorAll(tag)].filter((heading) => heading.textContent.trim()));
    return levels.find((headings) => headings.length > 1) || levels.find((headings) => headings.length) || [];
  };

  const createTocToggle = (navigation, labelText, count) => {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'cl-legal-toc__label';
    toggle.setAttribute('aria-expanded', 'false');

    const label = document.createElement('span');
    label.textContent = labelText;
    const counter = document.createElement('span');
    counter.className = 'cl-legal-toc__count';
    counter.textContent = `(${count})`;
    label.append(counter);

    toggle.append(label);
    toggle.insertAdjacentHTML('beforeend', '<svg class="cl-legal-toc__chevron" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m2.5 4.5 3.5 3.5 3.5-3.5"/></svg>');

    toggle.addEventListener('click', () => {
      const open = navigation.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });

    navigation.addEventListener('click', (event) => {
      if (!event.target.closest('a')) return;
      navigation.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    });

    return toggle;
  };

  const removeRepeatedTitle = (root, content) => {
    const pageTitle = root.querySelector('h1')?.textContent.trim().toLocaleLowerCase();
    const first = content.firstElementChild;
    if (!pageTitle || !first || first.matches('h1, h2, h3')) return;
    const text = first.textContent.trim().toLocaleLowerCase();
    if (text.length < 120 && text.includes(pageTitle)) first.remove();
  };

  const buildNavigation = (root, config) => {
    const content = root.querySelector('[data-legal-content], .shopify-policy__body .rte');
    const navigation = root.querySelector('[data-legal-toc]');
    if (!content || !navigation) return;

    removeRepeatedTitle(root, content);
    const headings = pickHeadings(content);
    if (!headings.length) return;

    const usedIds = new Set();
    navigation.append(createTocToggle(navigation, root.dataset.tocLabel || config.tocLabel || document.title, headings.length));

    headings.forEach((heading, index) => {
      const originalText = heading.textContent.trim().replace(/\s+/g, ' ');
      const articleMatch = originalText.match(NUMBER_PREFIX);
      const articleNumber = articleMatch && (articleMatch[1] || articleMatch[2]);
      const title = articleMatch ? originalText.slice(articleMatch[0].length) || originalText : originalText;
      heading.classList.add('cl-legal-main-heading');
      const baseId = heading.id || slugify(originalText) || `section-${index + 1}`;
      let id = baseId;
      let duplicate = 2;

      while (usedIds.has(id) || document.querySelectorAll(`#${CSS.escape(id)}`).length > 1) {
        id = `${baseId}-${duplicate}`;
        duplicate += 1;
      }

      usedIds.add(id);
      heading.id = id;

      if (!heading.querySelector('.cl-legal-number')) {
        if (articleMatch) heading.textContent = title;
        const number = document.createElement('span');
        number.className = 'cl-legal-number';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(articleNumber || index + 1).padStart(2, '0');
        heading.prepend(number);
        if (articleMatch) heading.setAttribute('aria-label', originalText);
      }

      const link = document.createElement('a');
      link.href = `#${id}`;
      link.textContent = title;
      navigation.append(link);
    });

    navigation.hidden = false;
  };

  const init = () => {
    const config = readConfig();
    const nativeRoot = prepareNativePolicy(config);
    const roots = [...document.querySelectorAll('[data-cl-legal-root]')];
    if (nativeRoot) roots.push(nativeRoot);
    roots.forEach((root) => buildNavigation(root, config));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
